import { Component, Input, OnInit, ViewChild } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Deals } from "src/app/pages/deals/models/deals.model";
import { EntityService } from "src/app/pages/entity/services/entity.service";
// @ts-ignore
import html2pdf from "html2pdf.js";
import { EventService } from "src/app/core/services/event.service";
import { environment } from "src/environments/environment";
import Swal from "sweetalert2";
import { FilesService } from "src/app/core/services/files.service";
import { PropertyDataService } from "src/app/core/services/property-data.service";
import { Subscription } from "rxjs";
import { auditTime } from "rxjs/operators";
import { PropertyValidationRulesService } from "src/app/core/services/propertyValidationRules.service";
const n2words = require("n2words").default;

/**
 * Gerador de CMI LEGADO (template HTML + html2pdf no browser).
 *
 * Cópia do modal que existia antes do runtime de templates PDF (commit
 * 051cde9b), mantida só para a lista antiga de negócios (/deals/board)
 * continuar a gerar o CMI enquanto os dois menus coexistem. O modal novo
 * (`app-mediation-contract`) usa o template configurado em admin/entidade.
 *
 * Não corrigir nem evoluir: desaparece com a lista antiga.
 */
@Component({
  selector: "app-mediation-contract-legacy",
  templateUrl: "./mediation-contract-legacy.component.html",
  styleUrls: ["./mediation-contract-legacy.component.scss"],
})
export class MediationContractLegacyComponent implements OnInit {
  private sub?: Subscription;
  @Input() property: any;
  @Input() deal: Deals | null = null;
  @Input() isEdit: boolean = false;
  @Input() isProperty: boolean = false;

  @ViewChild("mediationContractModal") mediationContractModal: any;

  entityData: any;
  mediationContractForm!: FormGroup;
  generateContract = false;
  logoPath = `${environment.apiUrl}/${environment.dirLogoImage}/`;
  spinnerLoading: boolean = false;

  constructor(
    private modalService: NgbModal,
    private entityService: EntityService,
    private formBuilder: FormBuilder,
    private eventService: EventService,
    private filesService: FilesService,
    private propertyData: PropertyDataService,
    private propertyValidationService: PropertyValidationRulesService,
  ) {}

  ngOnInit(): void {
    this.entityService.getEntityForProposal().subscribe((data) => {
      this.entityData = data;
      this.initForm(null);

      this.mediationContractForm.valueChanges
        .pipe(auditTime(150))
        .subscribe((value) => {
          this.propertyData.seedFromCMI(value);
        });

      this.mediationContractForm
        .get("businessPrice")
        ?.valueChanges.pipe(auditTime(150))
        .subscribe((value) => {
          const priceText = this.generateBusinessPriceWritten(value);
          this.mediationContractForm.patchValue(
            { businessPriceWritten: priceText },
            { emitEvent: false },
          );
        });

      this.mediationContractForm
        .get("documentsValue")
        ?.valueChanges.pipe(auditTime(150))
        .subscribe((value) => {
          const priceText = this.generateBusinessPriceWritten(value);
          this.mediationContractForm.patchValue(
            { documentsValueWritten: priceText },
            { emitEvent: false },
          );
        });

      if (this.isProperty) {
        this.patchFromTabs();
      } else {
        if (this.isEdit && this.deal?.id) {
          this.getMediation(this.deal.id);
        } else {
          this.openModal();
        }
      }
    });
  }

  getMediation(dealId: any) {
    if (dealId) {
      this.filesService.getMediationAgreement(dealId).subscribe((data) => {
        this.openModal(data);
      });
    } else {
      this.openModal();
    }
  }

  openModal(data: any = null) {
    let modal = this.modalService.open(this.mediationContractModal, {
      size: "xl",
      centered: true,
      backdrop: "static",
      backdropClass: "modal-z",
      windowClass: "modal-z",
    });
    modal.closed.subscribe(() => {
      this.eventService.broadcast("contract-generated");
    });
    modal.dismissed.subscribe(() => {
      this.eventService.broadcast("contract-generated");
    });
  }

  diffInMonths(start: Date, end: Date) {
    const years = end.getFullYear() - start.getFullYear();
    const months = end.getMonth() - start.getMonth();
    return years * 12 + months;
  }

  endStartDateChange(type: "start" | "end", event: any) {
    if (type === "start") {
      const cmiEndDate =
        this.mediationContractForm.get("endDateMediation")?.value;
      const cmiStartDate = event?.dateString;

      const diffMonths =
        cmiEndDate && cmiStartDate
          ? this.diffInMonths(new Date(cmiStartDate), new Date(cmiEndDate))
          : null;
      if (diffMonths) {
        this.mediationContractForm
          .get("contractDuration")
          ?.setValue(diffMonths);
      }
    } else {
      const cmiEndDate = event?.dateString;
      const cmiStartDate =
        this.mediationContractForm.get("startDateMediation")?.value;

      const diffMonths =
        cmiEndDate && cmiStartDate
          ? this.diffInMonths(new Date(cmiStartDate), new Date(cmiEndDate))
          : null;
      if (diffMonths) {
        this.mediationContractForm
          .get("contractDuration")
          ?.setValue(diffMonths);
      }
    }
  }

  initForm(data: any) {
    const cmiData = this.propertyData.currentCmiValue;
    const cmiEndDate = cmiData?.endDateMediation;
    const cmiStartDate = cmiData?.startDateMediation;

    const diffMonths =
      cmiEndDate && cmiStartDate
        ? Math.abs(
            new Date(cmiEndDate).getMonth() - new Date(cmiStartDate).getMonth(),
          )
        : 12;

    this.mediationContractForm = this.formBuilder.group({
      // 🔹 novos campos
      contractNumber: [data?.contractNumber || cmiData?.contractNumber || ""],
      startDateMediation: [
        data?.startDateMediation || cmiData?.startDateMediation || null,
      ],
      endDateMediation: [
        data?.endDateMediation || cmiData?.endDateMediation || null,
      ],
      defineEndDate: [data?.defineEndDate ?? null],
      autoRenewal: [data?.autoRenewal || cmiData?.autoRenewal || null],
      entityName: [
        data?.entityName || this.entityData?.name,
        Validators.required,
      ],
      entityAddress: [
        data?.entityAddress || this.entityData?.address,
        Validators.required,
      ],
      entityCapital: [data?.entityCapital || "", Validators.required],
      entityFiscalIdentification: [
        data?.entityFiscalIdentification ||
          this.entityData?.fiscalIdentification,
        Validators.required,
      ],
      conservatory: [data?.conservatory || "", Validators.required],
      conservatoryNumber: [data?.conservatoryNumber || "", Validators.required],
      licenseImo: [
        data?.licenseImo || this.entityData?.license,
        Validators.required,
      ],
      ownerName: [data?.ownerName || this?.deal?.name, Validators.required],
      maritalStatus: [data?.maritalStatus || ""],
      propertyRegime: [data?.propertyRegime || ""],
      spouse: [data?.spouse || ""],
      address: [data?.address || "", Validators.required],
      city: [data?.city || "", Validators.required],
      ownerIdentification: [
        data?.ownerIdentification || "",
        Validators.required,
      ],
      ownerIdentification2: [data?.ownerIdentification2 || ""],
      ownerFiscalIdentification: [
        data?.ownerFiscalIdentification || "",
        Validators.required,
      ],
      ownerFiscalIdentification2: [data?.ownerFiscalIdentification2 || ""],
      phone: [data?.phone || this?.deal?.phone],
      email: [data?.email || this?.deal?.email],
      ownerRole: [data?.ownerRole || "OWNER", Validators.required],
      propertyType: [data?.propertyType || "", Validators.required],
      divisions: [data?.divisions || "", Validators.required],
      area: [data?.area || "", Validators.required],
      propertyAddress: [data?.propertyAddress || "", Validators.required],
      propertyCity: [data?.propertyCity || "", Validators.required],
      parish: [data?.parish || "", Validators.required],
      county: [data?.county || "", Validators.required],
      registryOffice: [data?.registryOffice || ""],
      registryNumber: [data?.registryNumber || ""],
      constructionLicense: [data?.constructionLicense || ""],
      cityCouncil: [data?.cityCouncil || ""],
      issueDate: [data?.issueDate || new Date()],
      matrixArticle: [data?.matrixArticle || ""],
      matrixParish: [data?.matrixParish || ""],
      matrixOmitted: [data?.matrixOmitted || ""],
      businessBuy: [data?.businessBuy ?? true],
      businessTransfer: [data?.businessTransfer ?? false],
      businessRent: [data?.businessRent ?? false],
      businessOther: [data?.businessOther ?? false],
      businessOtherDescription: [data?.businessOtherDescription || ""],
      businessPrice: [data?.businessPrice || ""],
      businessPriceWritten: [
        (data?.businessPriceWritten ?? data?.businessPrice)
          ? this.generateBusinessPriceWritten(data?.businessPrice)
          : "",
      ],
      onus: [data?.onus ?? false],
      onusDescription: [data?.onusDescription || ""],
      onusValue: [data?.onusValue || ""],
      contractRegime: [data?.contractRegime ?? false],
      remunerationType: [data?.remunerationType || ""],
      percentMin: [data?.percentMin ?? 0],
      minValue: [data?.minValue ?? 0],
      taxMin: [data?.taxMin ?? 23],
      percent: [data?.percent ?? 0],
      tax: [data?.tax ?? 23],
      paymentConditions: [data?.paymentConditions || "TotalPromessa"],
      percentPromise: [data?.percentPromise ?? 0],
      percentDeed: [data?.percentDeed ?? 0],
      documentsRemuneration: [data?.documentsRemuneration || ""],
      documentsValue: [data?.documentsValue || ""],
      documentsValueWritten: [data?.documentsValueWritten || ""],
      insurance: [data?.insurance ?? 0],
      policy: [data?.policy || ""],
      insurer: [data?.insurer || ""],
      contractDuration: [data?.contractDuration || diffMonths || 12],
      terminationNotice: [data?.terminationNotice || ""],
      collaborationDeadline: [data?.collaborationDeadline || ""],
      agentName: [
        data?.agentName || this.deal?.agent?.name || "",
        Validators.required,
      ],
      agentFiscalIdentification: [data?.agentFiscalIdentification || ""],
      jurisdiction: [data?.jurisdiction || ""],
      disputeCenter: [data?.disputeCenter || ""],
      dataAuthorization: [data?.dataAuthorization ?? true],
    });

    // se já vier com start + defineEndDate preenchidos, calcula o end
    const months = this.mediationContractForm.get("defineEndDate")?.value;

    const start = this.mediationContractForm.get("startDateMediation")?.value;

    if (months && start) {
      const s = new Date(start);
      const e = new Date(s);
      e.setMonth(s.getMonth() + Number(months));
      this.mediationContractForm.patchValue({ endDateMediation: e });
    }
  }

  get form() {
    return this.mediationContractForm.controls;
  }

  async submitContract() {
    if (!this.mediationContractForm.valid) return;
    this.spinnerLoading = true;
    this.generateContract = true;

    await new Promise((resolve) => setTimeout(resolve, 0));

    setTimeout(() => {
      const element = document.getElementById("mediation-contract");

      if (!element) return;

      const opt = {
        margin: [10, 10, 10, 10],
        filename: `contrato-de-mediacao-${this.form["contractNumber"].value}.pdf`,
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: false,
        },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
      };

      html2pdf()
        .from(element)
        .set(opt)
        .outputPdf("blob")
        .then((pdfBlob: any) => {
          const formData = new FormData();
          formData.append(
            "file",
            pdfBlob,
            `contrato-de-mediacao-${this.form["contractNumber"].value}.pdf`,
          );

          Object.keys(this.mediationContractForm.value).forEach((key) => {
            if (
              this.mediationContractForm.value[key] !== null &&
              this.mediationContractForm.value[key] !== "" &&
              ![
                "startDateMediation",
                "endDateMediation",
                "defineEndDate",
              ].includes(key)
            )
              formData.append(key, this.mediationContractForm.value[key]);
          });

          if (this.deal?.property?.id)
            formData.append("propertyId", this.deal?.property?.id);

          formData.append("dealId", this.deal?.id);

          if (!this.isEdit) {
            this.filesService.createMediationAgreement(formData).subscribe({
              next: () => {
                this.spinnerLoading = false;
                element?.classList.add("d-none");
                const url = URL.createObjectURL(pdfBlob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `contrato-de-mediacao-${this.form["contractNumber"].value}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                this.eventService.broadcast("contract-generated");
                this.generateContract = false;
                this.modalService.dismissAll();
              },
              error: (err) => {
                this.spinnerLoading = false;
                Swal.fire({
                  title: "Erro ao gerar contrato",
                  text: "Ocorreu um erro ao guardar o contrato de mediação. Tente novamente.",
                  icon: "error",
                  confirmButtonText: "OK",
                });
              },
            });
          } else {
            this.filesService
              .updateMediationAgreement(formData, this.deal?.id)
              .subscribe({
                next: () => {
                  this.spinnerLoading = false;
                  element?.classList.add("d-none");
                  const url = URL.createObjectURL(pdfBlob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `contrato-de-mediacao-${this.form["contractNumber"].value}.pdf`;
                  a.click();
                  URL.revokeObjectURL(url);
                  this.eventService.broadcast("contract-generated");
                  this.generateContract = false;
                  this.modalService.dismissAll();
                },
                error: (err) => {
                  this.spinnerLoading = false;
                  Swal.fire({
                    title: "Erro ao gerar contrato",
                    text: "Ocorreu um erro ao guardar o contrato de mediação. Tente novamente.",
                    icon: "error",
                    confirmButtonText: "OK",
                  });
                },
              });
          }
        });

      // Swal.fire({
      //   title: 'Contrato Gerado',
      //   text: 'O contrato de mediação foi gerado com sucesso, deseja fechar a janela do Contrato de Mediação ou realizar alguma alteração?',
      //   icon: 'success',
      //   showCancelButton: true,
      //   confirmButtonText: 'Fechar',
      //   cancelButtonText: 'Alterar',
      //   reverseButtons: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     this.eventService.broadcast('contract-generated');
      //     this.generateContract = false;
      //     this.modalService.dismissAll();
      //   }
      // });
    });
  }

  onChangeDefineDate(event: any) {
    let dateStart = new Date();

    if (this.mediationContractForm.get("startDateMediation")?.value) {
      dateStart = new Date(
        this.mediationContractForm.get("startDateMediation")?.value,
      );
    } else {
      this.mediationContractForm.patchValue({ startDateMediation: dateStart });
    }
    let dateEnd = new Date(dateStart);
    dateEnd.setMonth(dateStart.getMonth() + event);
    if (event)
      this.mediationContractForm.patchValue({ endDateMediation: dateEnd });
  }

  onChangeStartDateMediation(event: any) {
    if (
      event?.target?.value &&
      this.mediationContractForm.get("defineEndDate")?.value > 0
    ) {
      const dateStart = new Date(event.target.value);
      const dateEnd = new Date(dateStart);
      dateEnd.setMonth(
        dateStart.getMonth() +
          this.mediationContractForm.get("defineEndDate")?.value,
      );
      this.mediationContractForm.patchValue({ endDateMediation: dateEnd });
    }
  }

  private patchFromTabs() {
    if (!this.mediationContractForm) return;

    this.propertyData.propertyFormData$.subscribe((fd) => {
      if (!fd) return;

      /** 1️⃣ DETAILS TAB **/
      if (fd.details) {
        const d = fd.details;
        const saleCommission = d?.isPercentageCommission
          ? d?.saleCommission
          : (d?.saleCommission * 100) / d?.price;
        this.mediationContractForm.patchValue(
          {
            // propertyType: d.propertyType ?? "",
            yearOfConstruction: d.yearOfConstruction ?? "",
            condition: d.condition ?? "",
            businessPrice: d.price ?? "",
            businessPriceWritten:
              d?.price && d?.price > 0
                ? this.generateBusinessPriceWritten(d.price)
                : "",
            currency: d.currency ?? "",
            exclusivity: d.exclusive,
            contractRegime: d.exclusive ?? false,
            reference: d.reference ?? "",
            ecNumber: d.ecNumber ?? "",
            ecExpirationDate: d.ecExpirationDate ?? "",
            renewableEnergy: d.renewableEnergy ?? "",
            carbonEmissions: d.carbonEmissions ?? "",
            saleCommission: d.saleCommission ?? "",
            percent: saleCommission ?? 0,
            isPercentageCommission:
              d.isPercentageCommission === undefined
                ? true
                : d.isPercentageCommission,
            sharedCommission: d.sharedCommission ?? "",
            partnerEntityName: d.partnerEntityName ?? "",
            useLicense: d.useLicense ?? "",
            buildingLicense: d.buildingLicense ?? "",
            paymentConditions:
              d.paymentsConditions && d.paymentsConditions.length > 0
                ? "Parcial"
                : "TotalPromessa",
            percentPromise:
              d.paymentsConditions &&
              d?.paymentsConditions?.find(
                (c: any) => c.type === "SALE_AGREEMENT",
              )
                ? d?.paymentsConditions?.find(
                    (c: any) => c.type === "SALE_AGREEMENT",
                  )?.value
                : 0,
            percentDeed:
              d.paymentsConditions &&
              d?.paymentsConditions?.find((c: any) => c.type === "DEED")
                ? d?.paymentsConditions?.find((c: any) => c.type === "DEED")
                    ?.value
                : 0,
            businessBuy: ![2, 3, 7, 10].includes(d?.businessType)
              ? true
              : false,
            businessTransfer: d?.businessType === 3 ? true : false,
            businessRent: d?.businessType === 2 ? true : false,
          },
          { emitEvent: false },
        );
      }

      /** 2️⃣ ADDRESS TAB **/
      if (fd.address) {
        const a = fd.address;
        this.mediationContractForm.patchValue(
          {
            propertyAddress: a.address ?? "",
            propertyCity: a.districtName ?? "",
            county: a.countyName ?? a.district ?? "",
            parish: a.parishName ?? "",
            zipCode: a.zipCode ?? "",
            city: a.city,
            doorNumber: a.doorNumber ?? "",
            fraction: a.fraction ?? "",
            floor: a.floor ?? "",
          },
          { emitEvent: false },
        );
      }

      /** 3️⃣ AREAS / NEARBY TAB **/
      if (fd.areasNearby) {
        const area = fd.areasNearby;
        const total = Object.keys(area)
          .filter(
            (k) =>
              k.startsWith("division") &&
              !k.includes("division9") &&
              !k.includes("division6"),
          )
          .reduce((sum, k) => sum + (area[k] || 0), 0);

        this.mediationContractForm.patchValue(
          {
            area: area.grossArea ?? area.usefulArea ?? "",
            //soma as divisões pelo valor
            divisions: total ?? "",
          },
          { emitEvent: false },
        );
      }

      /** 4️⃣ CONTACTS TAB — OWNERS **/
      const owners = fd?.contacts?.owners;
      if (Array.isArray(owners) && owners.length > 0) {
        const owner = owners[0];

        this.mediationContractForm.patchValue(
          {
            ownerName: `${owner.firstName ?? ""} ${
              owner.lastName ?? ""
            }`.trim(),
            maritalStatus: owner.maritalStatus ?? "",
            propertyRegime: owner.propertyRegime ?? "",
            // spouse: owner.spouse ?? "",
            address: owner.fiscalAddress ?? owner.address ?? "",
            city: owner.city ?? owner.fiscalCity ?? "",
            email: owner.email ?? "",
            phone: owner.phoneNumber1 ?? owner.phone ?? "",
            ownerIdentification: owner.identificationDocument ?? "",
            ownerFiscalIdentification: owner.fiscalIdentification,
          },
          { emitEvent: false },
        );
      }

      /** 6️⃣ AGENTS TAB **/
      if (fd.agents?.mainAgent) {
        const ag = fd.agents.mainAgent;

        this.mediationContractForm.patchValue(
          {
            agentName: ag.name ?? "",
            agentFiscalIdentification: ag.fiscalIdentification ?? "",
          },
          { emitEvent: false },
        );
      }

      /** 8️⃣ PROPOSALS TAB **/
      if (fd.proposals) {
        const p = Array.isArray(fd.proposals) ? fd.proposals[0] : fd.proposals;
        if (p) {
          this.mediationContractForm.patchValue(
            {
              proposalValue: p.value ?? "",
              proposalDate: p.proposalDate ?? "",
              partnerName: p.partnerName ?? "",
              licenseImo: p.licenseImo ?? this.entityData?.licenseImo ?? "",
            },
            { emitEvent: false },
          );
        }
      }
    });
  }

  generateBusinessPriceWritten(price: number) {
    if (!price) return "";
    price = Number(price);
    const int = Math.floor(price);
    const dec = Math.round((price - int) * 100);

    const extInt = n2words(int, { lang: "pt-PT" });
    const extDec = n2words(dec, { lang: "pt-PT" });

    const result =
      `${extInt} euro${extDec !== 1 ? "s" : ""}` +
      (dec > 0 ? ` e ${extDec} cêntimo${dec !== 1 ? "s" : ""}` : "");

    return result.charAt(0).toUpperCase() + result.slice(1);
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
